import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Paper,
  Slide,
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
} from '@mui/icons-material';
import * as AudioRecorderModule from "../../../hooks/useAudioRecorder";
import {
  formatDurationLabel,
  getAudioDurationSeconds,
} from "../../../utils/audio";
import {
  getAudioPolicy,
  msToDurationString,
} from "../../../../../../shared/config/recordingPolicy";

// Handle ES6 default export
const AudioRecorder =
  (AudioRecorderModule as any).default || AudioRecorderModule.AudioRecorder;

// Define type for AudioRecorder
type AudioRecorderType = any;

interface AudioRecordingBarProps {
  show: boolean;
  onSendAudio: (blob: Blob, buffer: ArrayBuffer, duration?: string) => Promise<void>;
  onCancel: () => void;
}

const AudioRecordingBar: React.FC<AudioRecordingBarProps> = ({
  show,
  onSendAudio,
  onCancel,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [recordedBuffer, setRecordedBuffer] = useState<ArrayBuffer | null>(null); // Store buffer from getMp3()
  const [limitWarning, setLimitWarning] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  
  const recorderRef = useRef<AudioRecorderType | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioPolicy = useMemo(() => getAudioPolicy(), []);
  const maxDurationSeconds = useMemo(
    () => Math.floor(audioPolicy.maxDurationMs / 1000),
    [audioPolicy.maxDurationMs],
  );
  const warnThresholdSeconds = useMemo(
    () => Math.floor(audioPolicy.warnAtMs / 1000),
    [audioPolicy.warnAtMs],
  );
  const maxDurationLabel = useMemo(
    () => msToDurationString(audioPolicy.maxDurationMs),
    [audioPolicy.maxDurationMs],
  );
  const limitWarningIssuedRef = useRef(false);
  const limitReachedRef = useRef(false);
  const warnTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hardStopTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearLimitTimers = useCallback(() => {
    if (warnTimeoutRef.current) {
      clearTimeout(warnTimeoutRef.current);
      warnTimeoutRef.current = null;
    }
    if (hardStopTimeoutRef.current) {
      clearTimeout(hardStopTimeoutRef.current);
      hardStopTimeoutRef.current = null;
    }
  }, []);

  const resetLimitTracking = useCallback(() => {
    limitWarningIssuedRef.current = false;
    limitReachedRef.current = false;
    setLimitWarning(false);
    clearLimitTimers();
  }, [clearLimitTimers]);

  // Format time as MM:SS
  const formatTime = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Stop recording
  const stopRecording = useCallback(
    async (shouldSave = true) => {
      const recorder = recorderRef.current;
      if (!recorder) {
        return;
      }

      const isRecorderActive =
        typeof recorder.isActive === 'boolean'
          ? recorder.isActive
          : typeof recorder.state === 'string'
            ? recorder.state !== 'inactive'
            : true;

      if (!isRecorderActive) {
        return;
      }

      try {
        const [buffer, blob] = await recorder.getMp3();

        if (shouldSave) {
          setAudioBlob(blob);
          setRecordedBuffer(buffer); // Store buffer for later use

          // Create audio URL for preview
          if (audioRef.current) {
            const audioUrl = URL.createObjectURL(blob);
            audioRef.current.src = audioUrl;
          }

          setHasRecording(true);
        } else if (blob) {
          // If not saving, clean up the blob
          try {
            const tempUrl = URL.createObjectURL(blob);
            URL.revokeObjectURL(tempUrl);
          } catch (cleanupError) {
            console.warn("Failed to revoke temp audio URL", cleanupError);
          }
        }
      } catch (error) {
        console.error("Failed to stop recording:", error);
        setError("Failed to process recording");
      } finally {
        setIsRecording(false);
        clearLimitTimers();

        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    },
    [clearLimitTimers],
  );

  const handleDurationThresholds = useCallback(
    (elapsedSeconds: number) => {
      if (limitReachedRef.current) {
        return;
      }

      if (
        !limitWarningIssuedRef.current &&
        warnThresholdSeconds > 0 &&
        elapsedSeconds >= warnThresholdSeconds
      ) {
        limitWarningIssuedRef.current = true;
        setLimitWarning(true);
      }

      if (
        !limitReachedRef.current &&
        maxDurationSeconds > 0 &&
        elapsedSeconds >= maxDurationSeconds
      ) {
        limitReachedRef.current = true;
        setLimitWarning(true);
        setError(
          `Maximum recording length reached (${maxDurationLabel}).`,
        );
        void stopRecording(true);
      }
    },
    [
      maxDurationSeconds,
      maxDurationLabel,
      stopRecording,
      warnThresholdSeconds,
    ],
  );

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      resetLimitTracking();
      setError(null);
      setIsInitializing(true);

      // If we're re-recording, clean up previous recording
      if (audioBlob && audioRef.current) {
        if (audioRef.current.src) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.pause();
        setAudioBlob(null);
        setHasRecording(false);
        setIsPlaying(false);
      }

      const mic = new AudioRecorder({ bitRate: 128 });
      recorderRef.current = mic;

      await mic.start();
      setIsInitializing(false);
      setIsRecording(true);
      setRecordingTime(0);

      if (audioPolicy.warnAtMs > 0) {
        warnTimeoutRef.current = setTimeout(() => {
          limitWarningIssuedRef.current = true;
          setLimitWarning(true);
        }, audioPolicy.warnAtMs);
      }

      if (audioPolicy.maxDurationMs > 0) {
        hardStopTimeoutRef.current = setTimeout(() => {
          limitReachedRef.current = true;
          setLimitWarning(true);
          setError(`Maximum recording length reached (${maxDurationLabel}).`);
          void stopRecording(true);
        }, audioPolicy.maxDurationMs);
      }

      // Start timer
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const next = prev + 1;
          handleDurationThresholds(next);
          return next;
        });
      }, 1000);
    } catch (err) {
      console.error("Failed to start recording:", err);
      setIsInitializing(false);
      setError(
        "Failed to start recording. Please check microphone permissions.",
      );
    }
  }, [
    audioBlob,
    audioPolicy.maxDurationMs,
    audioPolicy.warnAtMs,
    handleDurationThresholds,
    maxDurationLabel,
    resetLimitTracking,
    stopRecording,
  ]);

  // Play/pause preview
  const togglePlayback = useCallback(() => {
    if (!audioRef.current || !audioBlob) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        console.error("Failed to play audio:", err);
        setError("Failed to play audio preview");
      });
    }
  }, [isPlaying, audioBlob]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    // Stop recording if currently recording, but don't save it
    if (isRecording) {
      stopRecording(false);
    }

    // Clean up timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Clean up recorder - create fresh instance
    if (recorderRef.current) {
      try {
        recorderRef.current = new AudioRecorder({ bitRate: 128 });
      } catch (error) {
        console.warn("Error resetting recorder:", error);
      }
    }

    // Clean up audio playback
    if (audioRef.current) {
      audioRef.current.pause();
      if (audioRef.current.src) {
        URL.revokeObjectURL(audioRef.current.src);
        audioRef.current.src = "";
      }
    }

    // Reset all state completely
    setIsRecording(false);
    setRecordingTime(0);
    setError(null);
    setHasRecording(false);
    setIsSending(false);
    setIsPlaying(false);
    setIsInitializing(false);
    setAudioBlob(null);
    setRecordedBuffer(null); // Clear stored buffer
    resetLimitTracking();

    // Call parent cancel handler
    onCancel();
  }, [isRecording, onCancel, resetLimitTracking, stopRecording]);

  // Send recording
  const sendRecording = useCallback(async () => {
    if (!audioBlob || !recordedBuffer || !hasRecording) return;

    setIsSending(true);
    try {
      let durationSeconds = 0;
      try {
        durationSeconds = await getAudioDurationSeconds(recordedBuffer);
      } catch (durationError) {
        console.warn("Failed to calculate audio duration", durationError);
      }

      const fallbackDuration = durationSeconds > 0 ? durationSeconds : recordingTime;
      const formattedDuration = formatDurationLabel(fallbackDuration);

      // Use the stored blob and buffer (no second getMp3() call!)
      await onSendAudio(audioBlob, recordedBuffer, formattedDuration);

      handleCancel(); // Reset state after sending
    } catch (error) {
      console.error("Failed to send audio:", error);
      setError("Failed to send audio message");
    } finally {
      setIsSending(false);
    }
  }, [audioBlob, recordedBuffer, hasRecording, onSendAudio, recordingTime, handleCancel]);

  // Auto-start recording when bar shows
  useEffect(() => {
    if (show && !isRecording && !hasRecording) {
      setIsInitializing(true);
      startRecording();
    } else if (!show) {
      // Reset initialization state when component is hidden
      setIsInitializing(false);
    }
  }, [show, isRecording, hasRecording, startRecording]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioBlob]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (recorderRef.current) {
        recorderRef.current.stop();
      }
      clearLimitTimers();
      resetLimitTracking();
    };
  }, [clearLimitTimers, resetLimitTracking]);

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          backgroundColor: '#ffffff',
          borderTop: '1px solid rgba(0, 119, 200, 0.1)',
          borderRadius: '12px 12px 0 0',
          boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Hidden audio element for preview */}
        <audio ref={audioRef} preload="metadata" />
        
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.5,
            gap: 1,
            minHeight: 60,
          }}
        >
          {/* Recording status indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {/* Animated recording dot */}
            {(isRecording || isInitializing) && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: isInitializing ? 'info.main' : 'error.main',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                    '50%': {
                      opacity: 0.5,
                      transform: 'scale(1.2)',
                    },
                    '100%': {
                      opacity: 1,
                      transform: 'scale(1)',
                    },
                  },
                }}
              />
            )}

            {/* Status text and timer */}
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {error ? 'Recording Failed' : 
                 isInitializing ? 'Initializing audio... Please stand by' :
                 isRecording ? 'Recording...' : 
                 isPlaying ? 'Playing Preview...' :
                 hasRecording ? 'Ready to Send' : 'Starting...'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontFamily: 'monospace',
                  color: isRecording ? 'error.main' : isPlaying ? 'warning.main' : 'text.secondary',
                  fontWeight: 'bold'
                }}
              >
                {formatTime(recordingTime)}
              </Typography>
              {maxDurationSeconds > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontSize: '10px',
                    color: limitWarning ? 'warning.main' : 'text.secondary',
                    fontWeight: limitWarning ? 'bold' : 'normal',
                  }}
                >
                  {limitWarning
                    ? `Approaching max (${maxDurationLabel})`
                    : `Max ${maxDurationLabel}`}
                </Typography>
              )}
              {hasRecording && !isRecording && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '10px' }}>
                  Preview • Re-record • Send
                </Typography>
              )}
            </Box>
          </Box>

          {/* Progress bar */}
          {(isRecording || isInitializing) && (
            <Box sx={{ flex: 1, mx: 2 }}>
              <LinearProgress 
                color={isInitializing ? "info" : "error"}
                sx={{ 
                  height: 3, 
                  borderRadius: 2,
                  backgroundColor: isInitializing 
                    ? 'rgba(33, 150, 243, 0.1)' 
                    : 'rgba(244, 67, 54, 0.1)',
                }} 
              />
            </Box>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Cancel/Delete button */}
            <IconButton
              onClick={handleCancel}
              disabled={isSending}
              size="small"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  color: 'error.main',
                },
              }}
            >
              {hasRecording ? <DeleteIcon /> : <CloseIcon />}
            </IconButton>

            {/* Preview button - only show when recording is available */}
            {hasRecording && audioBlob && (
              <IconButton
                onClick={togglePlayback}
                disabled={isSending}
                size="small"
                sx={{
                  color: isPlaying ? 'warning.main' : 'info.main',
                  backgroundColor: isPlaying ? 'rgba(255, 152, 0, 0.1)' : 'rgba(2, 136, 209, 0.1)',
                  '&:hover': {
                    backgroundColor: isPlaying ? 'rgba(255, 152, 0, 0.2)' : 'rgba(2, 136, 209, 0.2)',
                  },
                  width: 32,
                  height: 32,
                }}
              >
                {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
              </IconButton>
            )}

            {/* Stop/Start recording button */}
            <IconButton
              onClick={isRecording ? () => stopRecording(true) : startRecording}
              disabled={isSending}
              sx={{
                backgroundColor: isRecording ? 'error.main' : 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: isRecording ? 'error.dark' : 'primary.dark',
                },
                '&:disabled': {
                  backgroundColor: 'action.disabled',
                  color: 'action.disabled',
                },
                width: 40,
                height: 40,
              }}
            >
              {isRecording ? <StopIcon /> : <MicIcon />}
            </IconButton>

            {/* Send button */}
            <IconButton
              onClick={sendRecording}
              disabled={!hasRecording || isSending}
              sx={{
                backgroundColor: hasRecording ? 'success.main' : 'action.disabled',
                color: 'white',
                '&:hover': {
                  backgroundColor: hasRecording ? 'success.dark' : 'action.disabled',
                },
                '&:disabled': {
                  backgroundColor: 'action.disabled',
                },
                width: 40,
                height: 40,
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Error message */}
        {error && (
          <Box sx={{ px: 2, pb: 1 }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Box>
        )}
      </Paper>
    </Slide>
  );
};

export default AudioRecordingBar;
